import { useEffect, useState } from "react";
import { FolderKanban, Loader2, Plus, Save } from "lucide-react";
import { toast, Toaster } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_FLOW = ["em produção", "em revisão", "entregue"];
const STATUS_STYLE = {
  "em produção": "border-[#E50914]/40 bg-[#E50914]/10 text-[#FF2A3B]",
  "em revisão": "border-amber-500/40 bg-amber-500/10 text-amber-400",
  entregue: "border-emerald-500/40 bg-emerald-500/10 text-emerald-400",
};

export const AdminProjects = () => {
  const [projects, setProjects] = useState(null);
  const [email, setEmail] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [creating, setCreating] = useState(false);
  const [urls, setUrls] = useState({});

  const load = () =>
    fetch(`${API}/admin/projects`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setProjects(d.projects || []))
      .catch(() => setProjects([]));

  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const res = await fetch(`${API}/admin/projects`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ client_email: email, title, description }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Erro ao criar");
      toast.success("Projeto criado! O cliente já vê na conta dele.");
      setEmail("");
      setTitle("");
      setDescription("");
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCreating(false);
    }
  };

  const update = async (projectId, body) => {
    await fetch(`${API}/admin/projects/${projectId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(body),
    }).catch(() => {});
    load();
  };

  return (
    <div className="mt-10 rounded-2xl border border-[#22222A] bg-[#101014]">
      <div className="flex items-center gap-3 border-b border-[#1A1A20] px-6 py-4">
        <FolderKanban className="h-5 w-5 text-[#E50914]" />
        <div>
          <h2 className="font-display text-lg font-bold text-zinc-100">Projetos de clientes</h2>
          <p className="text-xs text-zinc-500">Crie e atualize o status que o cliente acompanha na conta dele</p>
        </div>
      </div>

      <form onSubmit={create} className="grid grid-cols-1 gap-3 border-b border-[#1A1A20] p-6 sm:grid-cols-[1fr_1fr_auto]">
        <input
          data-testid="admin-project-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="E-mail do cliente (precisa ter conta)"
          className="h-11 rounded-xl border border-[#22222A] bg-[#050505] px-4 text-sm text-zinc-100 outline-none placeholder:text-zinc-600 focus:border-[#E50914]/60"
        />
        <input
          data-testid="admin-project-title"
          required
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Nome do projeto (ex: Site da Padaria)"
          className="h-11 rounded-xl border border-[#22222A] bg-[#050505] px-4 text-sm text-zinc-100 outline-none placeholder:text-zinc-600 focus:border-[#E50914]/60"
        />
        <button
          data-testid="admin-project-create-btn"
          type="submit"
          disabled={creating}
          className="flex h-11 items-center justify-center gap-2 rounded-full bg-[#E50914] px-6 font-mono-code text-xs font-bold uppercase tracking-widest text-white disabled:opacity-50"
        >
          {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          Criar
        </button>
        <textarea
          data-testid="admin-project-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          placeholder="Descrição curta (opcional)"
          className="resize-none rounded-xl border border-[#22222A] bg-[#050505] p-4 text-sm text-zinc-100 outline-none placeholder:text-zinc-600 focus:border-[#E50914]/60 sm:col-span-3"
        />
      </form>

      <div className="space-y-4 p-4 sm:p-6" data-testid="admin-projects-list">
        {projects === null ? (
          <p className="py-8 text-center text-sm text-zinc-600">Carregando...</p>
        ) : projects.length === 0 ? (
          <p className="py-8 text-center text-sm text-zinc-600">Nenhum projeto criado ainda.</p>
        ) : (
          projects.map((p) => {
            const next = STATUS_FLOW[STATUS_FLOW.indexOf(p.status) + 1];
            return (
              <div key={p.project_id} data-testid={`admin-project-${p.project_id.slice(-6)}`} className="rounded-xl border border-[#1A1A20] bg-[#0A0A0C] p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="font-display text-sm font-bold text-zinc-100">{p.title}</div>
                    <div className="mt-0.5 font-mono-code text-[11px] text-zinc-500">
                      {p.client_name} · {p.client_email}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`rounded-full border px-3 py-1 font-mono-code text-[10px] uppercase tracking-widest ${STATUS_STYLE[p.status]}`}>
                      {p.status}
                    </span>
                    {next && (
                      <button
                        data-testid={`admin-project-advance-${p.project_id.slice(-6)}`}
                        onClick={() => update(p.project_id, { status: next })}
                        className="rounded-full border border-[#2A2A33] px-3 py-1 font-mono-code text-[10px] uppercase tracking-widest text-zinc-400 transition-colors hover:border-[#E50914]/60 hover:text-white"
                      >
                        Avançar: {next}
                      </button>
                    )}
                  </div>
                </div>
                {p.description && <p className="mt-3 text-sm text-zinc-500">{p.description}</p>}
                {p.status === "entregue" && (
                  <div className="mt-4 flex gap-2">
                    <input
                      data-testid={`admin-project-url-${p.project_id.slice(-6)}`}
                      value={urls[p.project_id] ?? p.site_url ?? ""}
                      onChange={(e) => setUrls((prev) => ({ ...prev, [p.project_id]: e.target.value }))}
                      placeholder="Link do site entregue (https://...)"
                      className="h-10 flex-1 rounded-xl border border-[#22222A] bg-[#050505] px-4 text-sm text-zinc-100 outline-none placeholder:text-zinc-600 focus:border-[#E50914]/60"
                    />
                    <button
                      data-testid={`admin-project-url-save-${p.project_id.slice(-6)}`}
                      onClick={() => update(p.project_id, { site_url: urls[p.project_id] ?? p.site_url ?? "" })}
                      className="flex h-10 w-10 items-center justify-center rounded-xl border border-[#2A2A33] text-zinc-400 transition-colors hover:border-[#E50914] hover:text-white"
                      aria-label="Salvar link"
                    >
                      <Save className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
      <Toaster theme="dark" position="bottom-right" toastOptions={{ style: { background: "#101014", border: "1px solid #22222A", color: "#F4F4F6" } }} />
    </div>
  );
};
